import { useState } from "react";
import {
  InfiniteListBase,
  useListContext,
  useTranslate,
  useRedirect,
} from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { MobileListHeader } from "../../layout/MobileListHeader";
import { MobileContent } from "../../layout/MobileContent";
import { InfinitePagination } from "../../misc/InfinitePagination";
import { PaymentStatusBadge } from "./PaymentStatusBadge";
import { PaymentCreateSheet } from "./PaymentCreateSheet";
import type { Payment } from "../../types";

export const MobilePaymentList = () => (
  <InfiniteListBase
    perPage={50}
    sort={{ field: "planned_date", order: "ASC" }}
    filter={{ "status@neq": "paid" }}
  >
    <MobilePaymentListLayout />
  </InfiniteListBase>
);

const MobilePaymentListLayout = () => {
  const translate = useTranslate();
  const { data, isPending, error } = useListContext<Payment>();
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <>
      <MobileListHeader
        title={translate("resources.payments.name", { smart_count: 2 })}
        onCreateClick={() => setCreateOpen(true)}
      />
      <PaymentCreateSheet open={createOpen} onOpenChange={setCreateOpen} />
      <MobileContent>
        {isPending ? (
          <MobilePaymentSkeleton />
        ) : error ? (
          <p className="text-sm text-destructive">
            {translate("ra.message.error")}
          </p>
        ) : data?.length ? (
          <div className="space-y-3">
            {data.map((payment) => (
              <MobilePaymentCard key={payment.id} payment={payment} />
            ))}
          </div>
        ) : (
          <div className="py-16 text-center space-y-2">
            <p className="text-lg font-semibold">
              {translate("resources.payments.empty.title")}
            </p>
            <Button variant="outline" size="sm" onClick={() => setCreateOpen(true)}>
              {translate("resources.payments.action.new", { _: "Новый платёж" })}
            </Button>
          </div>
        )}
        <div className="flex justify-center mt-4">
          <InfinitePagination />
        </div>
      </MobileContent>
    </>
  );
};

const MobilePaymentCard = ({ payment }: { payment: Payment }) => {
  const redirect = useRedirect();

  const plannedDate = payment.planned_date
    ? new Date(payment.planned_date).toLocaleDateString("ru-RU")
    : null;
  const paidDate = payment.paid_date
    ? new Date(payment.paid_date).toLocaleDateString("ru-RU")
    : null;

  return (
    <Card
      className="py-3 cursor-pointer active:opacity-70"
      role="button"
      tabIndex={0}
      onClick={() => redirect(`/payments/${payment.id}/show`)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          redirect(`/payments/${payment.id}/show`);
        }
      }}
    >
      <CardContent className="px-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <span
            className={
              payment.direction === "expense"
                ? "font-semibold text-base text-rose-600"
                : "font-semibold text-base text-emerald-600"
            }
          >
            {payment.amount
              ? (payment.direction === "expense" ? "−" : "+") +
                Number(payment.amount).toLocaleString("ru-RU") +
                " ₽"
              : "—"}
          </span>
          <PaymentStatusBadge status={payment.status} />
        </div>
        {payment.vendor_name && (
          <div className="text-xs font-medium">{payment.vendor_name}</div>
        )}
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
          {plannedDate && <span>Планируется: {plannedDate}</span>}
          {paidDate && <span>Оплачено: {paidDate}</span>}
        </div>
        {payment.comment && (
          <p className="text-xs text-muted-foreground line-clamp-1">
            {payment.comment}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

const MobilePaymentSkeleton = () => (
  <div className="space-y-3">
    {Array.from({ length: 5 }).map((_, i) => (
      <Skeleton key={i} className="h-20 w-full rounded-xl" />
    ))}
  </div>
);
